import { http } from '@pendo/http';
import {
    FEATURE_PAGE_ADOPTION_METRICS,
    buildFeaturePageAdoptionAllUsersIncrease,
    buildFeaturePageAdoptionAllUsersDecrease,
    buildFeaturePageAdoptionNewUsers,
    parseFeaturePageAdoptionResults,
} from '@pendo/middleware';
import { i18n_t as $t } from '@/utils/i18n';
import moment from '@/utils/moment-wrapper';
import { createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util';
import { getPreviousTimeSeries } from '@/utils/time.utils';

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['revision', 'force'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function getAdoptionInsights (requestBody, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/insight/generate', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, requestBody, requestOptions).then((res) => res.data);
}

export const ADOPTION_METRICS = FEATURE_PAGE_ADOPTION_METRICS;

const COUNTABLE_KINDS = ['feature', 'page'];

const METRIC_BUILDERS = {
    [ADOPTION_METRICS.NEW_USERS]: buildFeaturePageAdoptionNewUsers,
    [ADOPTION_METRICS.ALL_USERS_INCREASE]: buildFeaturePageAdoptionAllUsersIncrease,
    [ADOPTION_METRICS.ALL_USERS_DECREASE]: buildFeaturePageAdoptionAllUsersDecrease,
};

export function getInitialState () {
    return {
        insights: null,
        insightRequestBody: null,
        isLoading: false,
        error: null,
    };
}

export function determineAppId (rootGetters) {
    // Remove this once we have config blade pulling the app id
    const defaultApp = rootGetters['apps/allWebPlusIntegratedMobileApps']?.[0];
    if (defaultApp?.id) {
        return defaultApp.id;
    }

    return -323232;
}

export function computeAdoptionTimeRangeStartAndEnd (offsetDays = 7) {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(offsetDays - 1, 'days');

    return {
        start: start.format('MMMM D'),
        end: end.format('MMMM D'),
    };
}

export function computeAdoptionTimeSeries (offsetDays = 7) {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(offsetDays - 1, 'days');

    return createTimeSeriesFromDate(
        { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') },
        true,
    );
}

export function computePreviousAdoptionTimeSeries (currentTimeSeries) {
    return getPreviousTimeSeries(currentTimeSeries);
}

export function buildRequestBody (appId, countableIds = [], includeAnonymous = false, { currentTimeSeries, previousTimeSeries, timeRangeStart, timeRangeEnd } = {}) {
    const jobSpec = {
        app_id: appId,
        metrics: [
            { metric_type: 'adoption_new_users' },
            { metric_type: 'adoption_all_users_increase' },
            { metric_type: 'adoption_all_users_decrease' },
        ],
        offset_days: 7,
        trend_type: 'adoption',
        countable_ids: countableIds,
        include_anonymous: includeAnonymous,
    };

    if (currentTimeSeries?.spec) {
        jobSpec.current_time_series = {
            first: currentTimeSeries.spec.first,
            period: currentTimeSeries.spec.period,
            count: currentTimeSeries.spec.count,
        };
    }

    if (previousTimeSeries?.spec) {
        jobSpec.previous_time_series = {
            first: previousTimeSeries.spec.first,
            period: previousTimeSeries.spec.period,
            count: previousTimeSeries.spec.count,
        };
    }

    return { job_spec: jobSpec, timeRangeStart, timeRangeEnd };
}

function buildAppIcon (rawIcon) {
    if (typeof rawIcon === 'string') {
        return { type: rawIcon, size: 12 };
    }
    if (rawIcon) {
        return { ...rawIcon, size: 12 };
    }

    return null;
}

function getCountableIcon (countableKind) {
    const kindMap = {
        Feature: 'mouse-pointer',
        Page: 'file',
    };

    return kindMap[countableKind] || 'feature';
}

function getTitle (countableKind) {
    const kindTitleMap = {
        Feature: $t('trends.Feature adoption'),
        Page: $t('trends.Page adoption'),
    };

    return kindTitleMap[countableKind] || $t('trends.Adoption');
}

function getLegacyTitle (metric, countableKind) {
    if (metric === 'adoption_all_users' || metric === 'adoption_new_users') {
        return getTitle(countableKind);
    }

    return $t('trends.Adoption');
}

function parseSummary (summary) {
    if (!summary || typeof summary !== 'string') {
        return { textBefore: summary || '', textAfter: '', showAppTag: false };
    }

    const tagNamePlaceholder = '{{COUNTABLE_NAME}}';
    const placeholderIndex = summary.indexOf(tagNamePlaceholder);

    if (placeholderIndex !== -1) {
        const textBefore = summary.substring(0, placeholderIndex).trim();
        const textAfter = summary.substring(placeholderIndex + tagNamePlaceholder.length).trim();

        return { textBefore, textAfter, showAppTag: true };
    }

    return { textBefore: summary, textAfter: '', showAppTag: false };
}

export function buildAppDataFromAdoptionState (state, rootGetters, fallbackAppId = null) {
    const adoptionInsightsArtifact = state?.artifacts?.adoption_insights;
    const responseAppId = adoptionInsightsArtifact?.data?.app_id || fallbackAppId;

    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(responseAppId);

    return {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId: responseAppId,
    };
}

export function transformInsightsResponse (finalState, appData) {
    const adoptionInsightsArtifact = finalState?.artifacts?.adoption_insights;
    const adoptionInsightsData = adoptionInsightsArtifact?.data;
    if (!adoptionInsightsData?.results || !Array.isArray(adoptionInsightsData.results)) {
        return [];
    }

    const appIcon = buildAppIcon(appData?.icon);

    return adoptionInsightsData.results.map((result) => {
        const { metric, countable_name: countableName, countable_kind: countableKind } = result;

        const tagName = countableName;
        const countableIcon = getCountableIcon(countableKind);
        const tagIcon = countableIcon ? { type: countableIcon, size: 12 } : appIcon;

        const title = getLegacyTitle(metric, countableKind);

        const { textBefore, textAfter, showAppTag } = parseSummary(result.summary);

        const trendDirection = result.metric === 'adoption_new_users' ? 'increase' : result.trend_direction;

        return {
            title,
            textBeforeTagName: textBefore,
            textAfterTagName: textAfter,
            showAppTag,
            tagName,
            tagIcon,
            appName: appData?.name,
            appIcon,
            delta: result.adoption_rate_change,
            trendDirection,
            adoptionRateCurrent: result.adoption_rate_current,
            adoptionRatePrevious: result.adoption_rate_previous,
            countableId: result.countable_id,
            countableKind,
            metric,
            type: 'adoption',
            appId: appData?.appId,
        };
    }).filter(Boolean);
}

export function buildAppData (rootGetters, appId) {
    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(appId);

    return {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId,
    };
}

export function splitCountableIdsByKind (countableIds = []) {
    const featureIds = [];
    const pageIds = [];

    countableIds.forEach((countable) => {
        if (!countable?.id || !countable?.kind) {
            return;
        }
        if (countable.kind === 'Feature') {
            featureIds.push(countable.id);
        } else if (countable.kind === 'Page') {
            pageIds.push(countable.id);
        }
    });

    return { featureIds, pageIds };
}

export function buildAdoptionAggregations ({ appId, hasShowAnonymousFlag, featureIds = [], pageIds = [] }) {
    const aggregations = [];

    Object.entries(METRIC_BUILDERS).forEach(([metric, builder]) => {
        COUNTABLE_KINDS.forEach((kind) => {
            const options = {
                appId,
                hasShowAnonymousFlag,
                featureIds: featureIds.length ? featureIds : undefined,
                pageIds: pageIds.length ? pageIds : undefined,
            };
            const { name, pipeline, productArea, subProductArea } = builder(options, kind);

            aggregations.push({
                metric,
                kind,
                name: `${name}-${kind}`,
                productArea,
                subProductArea,
                responseFormat: 'rows',
                build: () => pipeline,
            });
        });
    });

    return aggregations;
}

function buildAdoptionText (parsedResult, appData, hasSelectedEvents) {
    const { metric, adoptionRateCurrent, adoptionRatePrevious, delta } = parsedResult;
    const currentRate = adoptionRateCurrent ?? 0;
    const previousRate = adoptionRatePrevious ?? 0;
    const absChange = delta != null ? Math.abs(delta) : 0;

    if (metric === ADOPTION_METRICS.NEW_USERS) {
        const textBeforeTagName = hasSelectedEvents
            ? $t('signals.featurePageAdoptionNewUsersTextBeforeSelectedEvents')
            : $t('signals.featurePageAdoptionNewUsersTextBeforeApp', { appName: appData?.name ?? '' });

        return {
            textBeforeTagName,
            textAfterTagName: $t('signals.featurePageAdoptionNewUsersTextAfter', { currentRate }),
        };
    }

    if (metric === ADOPTION_METRICS.ALL_USERS_INCREASE) {
        return {
            textBeforeTagName: $t('signals.featurePageAdoptionTextBefore'),
            textAfterTagName: hasSelectedEvents
                ? $t('signals.featurePageAdoptionIncreaseTextAfterSelectedEvents', { absChange })
                : $t('signals.featurePageAdoptionIncreaseTextAfterApp', { absChange }),
        };
    }

    return {
        textBeforeTagName: $t('signals.featurePageAdoptionTextBefore'),
        textAfterTagName: hasSelectedEvents
            ? $t('signals.featurePageAdoptionDecreaseTextAfterSelectedEvents', { absChange, previousRate, currentRate })
            : $t('signals.featurePageAdoptionDecreaseTextAfterApp', { absChange, previousRate, currentRate }),
    };
}

function buildTrendFromParsedResult (parsedResult, appData, hasSelectedEvents) {
    const { metric, countableKind, countableId, countableName, adoptionRateCurrent, adoptionRatePrevious, delta, trendDirection } = parsedResult;

    const { textBeforeTagName, textAfterTagName } = buildAdoptionText(parsedResult, appData, hasSelectedEvents);

    const appIcon = buildAppIcon(appData?.icon);
    const tagIcon = { type: getCountableIcon(countableKind), size: 12 };

    return {
        title: getTitle(countableKind),
        textBeforeTagName,
        textAfterTagName,
        showAppTag: true,
        tagName: countableName,
        tagIcon,
        appName: appData?.name,
        appIcon,
        delta,
        trendDirection,
        adoptionRateCurrent,
        adoptionRatePrevious,
        countableId,
        countableKind,
        metric,
        type: 'adoption',
        appId: appData?.appId,
    };
}

export function transformAggregationResults (aggregations, rawResults, appData, hasSelectedEvents) {
    const parsedResults = parseFeaturePageAdoptionResults(aggregations, rawResults);

    return parsedResults.map((parsedResult) => buildTrendFromParsedResult(parsedResult, appData, hasSelectedEvents));
}
